import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const MatchPredictions = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasPredictions, setHasPredictions] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [upcomingGameweek, setUpcomingGameweek] = useState(null);
  const [isGW38, setIsGW38] = useState(false);
  const [seasonValue, setSeasonValue] = useState(null);

  useEffect(() => {
    const fetchMatchesAndDeadline = async () => {
      try {
        const [currentSeasonResponse, currentGameweekResponse, nextGameweekDeadlineResponse, teamsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/next-gw-deadline`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/teams`)
        ]);
        
        const currentSeason = currentSeasonResponse.data.season;
        setSeasonValue(currentSeason);

        const currentGameweek = currentGameweekResponse.data.gameweek;
        const nextGameweek = currentGameweek + 1;
        setUpcomingGameweek(nextGameweek);

        const fixturesResponse = await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/fixtures/${nextGameweek}`);
        const fixtures = fixturesResponse.data;

        const teams = teamsResponse.data;

        const processedMatches = fixtures.map(fixture => {
          const homeTeam = teams.find(team => team.id === fixture.team_h);
          const awayTeam = teams.find(team => team.id === fixture.team_a);
          return {
            id: fixture.code,
            homeTeam,
            awayTeam,
            homeScore: '',
            awayScore: ''
          };
        });

        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
          const db = getFirestore();
          const userDocRef = doc(db, 'match_predictions', `${user.uid}_${currentSeason}_${nextGameweek}`);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userPredictions = userDocSnap.data().predictions;
            const predictedMatches = processedMatches.map(match => {
              const prediction = userPredictions.find(pred => pred.id === match.id);
              return prediction ? { ...match, homeScore: prediction.homeScore.toString(), awayScore: prediction.awayScore.toString() } : match;
            });
            setMatches(predictedMatches);
            setHasPredictions(true);
          } else {
            setMatches(processedMatches);
          }
        } else {
          setMatches(processedMatches);
        }

        const deadlineDate = new Date(nextGameweekDeadlineResponse.data.deadline);
        setDeadline(deadlineDate);
        setIsDeadlinePassed(new Date() > deadlineDate);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('The Fantasy Premier League API is updating, please come back soon.');
        setLoading(false);
      }
    };
    fetchMatchesAndDeadline();
  }, []);

  useEffect(() => {
    if (deadline) {
      const timer = setInterval(() => {
        setIsDeadlinePassed(new Date() > deadline);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [deadline]);

  useEffect(() => {
    const checkSubmitDisabled = async () => {
      if (!hasPredictions) {
        setIsSubmitDisabled(false);
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setIsSubmitDisabled(false);
        return;
      }

      const db = getFirestore();
      const nextGameweek = (await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`)).data.gameweek + 1;

      if (nextGameweek === 39) {
        setIsGW38(true);
      }

      const userDocRef = doc(db, 'match_predictions', `${user.uid}_${seasonValue}_${nextGameweek}`);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userPredictions = userDocSnap.data().predictions;
        const predictedMatches = userPredictions.map(pred => ({
          id: pred.id,
          homeScore: pred.homeScore,
          awayScore: pred.awayScore
        }));

        const currentMatches = matches.map(match => ({
          id: match.id,
          homeScore: parseInt(match.homeScore) || 0,
          awayScore: parseInt(match.awayScore) || 0
        }));

        setIsSubmitDisabled(JSON.stringify(predictedMatches) === JSON.stringify(currentMatches));
      } else {
        setIsSubmitDisabled(false);
      }
    };

    checkSubmitDisabled();
  }, [matches, hasPredictions, seasonValue]);

  const handleScoreChange = (index, type, value) => {
    if (isDeadlinePassed) return;
    const newMatches = [...matches];
    newMatches[index][type] = value.replace(/[^0-9]/g, '');
    setMatches(newMatches);
  };

  const submitPredictions = async () => {
    if (isDeadlinePassed) {
      setMessage({ text: 'The deadline has passed. You can no longer submit predictions.', type: 'error' });
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      setMessage({ text: 'You must be logged in to submit predictions.', type: 'error' });
      return;
    }

    const db = getFirestore();
    const nextGameweek = (await axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-gameweek`)).data.gameweek + 1;
    const predictionsData = matches.map(match => ({
      id: match.id,
      homeScore: parseInt(match.homeScore) || 0,
      awayScore: parseInt(match.awayScore) || 0
    }));

    try {
      await setDoc(doc(db, 'match_predictions', `${user.uid}_${seasonValue}_${nextGameweek}`), {gameweek: nextGameweek, predictions: predictionsData, season: seasonValue, user_id: user.uid});
      const timestamp = new Date().toLocaleString();
      setMessage({ text: `Your predictions have been submitted successfully - (${timestamp})`, type: 'success' });
      setHasPredictions(true);
    } catch (error) {
      console.error('Error submitting predictions:', error);
      setMessage({ text: 'Failed to submit predictions. Please try again.', type: 'error' });
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-red-500 mb-4"></div>
        <div className="text-center text-red-500 text-xl font-bold">{error}</div>
      </div>
    );
  }

  if (isGW38) return (
    <div className="flex justify-center items-center h-screen">
      <div className="text-center text-premier-purple text-xl bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold mb-3">Thank you for playing this season!</h2>
        There are no further gameweeks to be predicted.
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-3xl"
    >
      <h1 className="text-4xl font-bold mb-6 text-premier-purple text-center">Premier League Match Predictor: Gameweek {upcomingGameweek}</h1>
      <p className="mt-2 text-gray-700 text-center mb-6">Visit the <Link to="/scoring-guide?openRulesOn=match" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for a breakdown of how points are scored.</p>

      {deadline && (
        <div className={`mb-4 p-4 rounded-lg text-center text-white ${isDeadlinePassed ? 'bg-red-500' : 'bg-green-500'}`}>
          {isDeadlinePassed
            ? 'The prediction window is now closed.'
            : `Gameweek ${upcomingGameweek} Deadline: ${deadline.toLocaleString()}`}
        </div>
      )}

      {message && (
        <div className={`mb-4 p-4 rounded-lg text-white text-center ${message.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {message.text}
        </div>
      )}

      {!isDeadlinePassed && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={submitPredictions}
          className="w-full mb-4 bg-premier-purple text-white py-6 px-4 font-bold text-lg rounded-lg shadow-lg hover:bg-premier-pink transition duration-300"
          disabled={isSubmitDisabled}
        >
          Submit Match Predictions
        </motion.button>
      )}

      <ul className="space-y-3">
        {matches.map((match, index) => (
          <motion.li
            key={match.id}
            layout
            transition={{ duration: 0.3 }}
            className="flex items-center justify-between bg-white p-3 sm:p-4 rounded-lg shadow-md"
          >
            <div className="flex items-center w-1/3 justify-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 mr-2 sm:mr-2 bg-gray-400 rounded-full flex items-center justify-center overflow-hidden">
                <img src={match.homeTeam.logo} alt={match.homeTeam.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              </div>
              <span className="text-gray-800 font-medium text-center text-sm sm:text-sm md:text-base">
                <span className="hidden sm:inline">{match.homeTeam.name}</span>
                <span className="sm:hidden">{match.homeTeam.short_name}</span>
              </span>
            </div>
            <div className="flex items-center justify-center w-1/3">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={match.homeScore}
                onChange={(e) => handleScoreChange(index, 'homeScore', e.target.value)}
                className="w-12 sm:w-16 p-2 sm:p-2 mr-2 sm:mr-2 border rounded text-center text-sm sm:text-base"
                disabled={isDeadlinePassed}
              />
              <span className="mx-2 sm:mx-2 text-sm sm:text-base">vs</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={match.awayScore}
                onChange={(e) => handleScoreChange(index, 'awayScore', e.target.value)}
                className="w-12 sm:w-16 p-2 sm:p-2 ml-2 sm:ml-2 border rounded text-center text-sm sm:text-base"
                disabled={isDeadlinePassed}
              />
            </div>
            <div className="flex items-center w-1/3 justify-end">
              <span className="text-gray-800 font-medium text-center text-sm sm:text-sm md:text-base">
                <span className="hidden sm:inline">{match.awayTeam.name}</span>
                <span className="sm:hidden">{match.awayTeam.short_name}</span>
              </span>
              <div className="w-10 h-10 sm:w-12 sm:h-12 ml-2 sm:ml-2 bg-gray-400 rounded-full flex items-center justify-center overflow-hidden">
                <img src={match.awayTeam.logo} alt={match.awayTeam.name} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              </div>
            </div>
          </motion.li>
        ))}
      </ul>
      <p className="text-gray-700 text-center mt-4">Don't forget to click the "Submit Match Predictions" button at the top of the page.</p>
    </motion.div>
  );
};

export default MatchPredictions;